import { Component, computed, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AmendmentActionsSubject } from '@app/features/amendments/amendment.model';
import { AmendmentService } from '@app/features/amendments/amendment.service';
import { ButtonAcceptAndRequestValidation } from '@app/features/contracts/validation/button-accept-and-request-validation/button-accept-and-request-validation';
import { ButtonAcceptValidationRequest } from '@app/features/contracts/validation/button-accept-validation-request/button-accept-validation-request';
import { ButtonCancelValidationRequest } from '@app/features/contracts/validation/button-cancel-validation-request/button-cancel-validation-request';
import { ButtonRefuseValidationRequest } from '@app/features/contracts/validation/button-refuse-validation-request/button-refuse-validation-request';
import { ButtonRequestValidation } from '@app/features/contracts/validation/button-request-validation/button-request-validation';
import { VALIDATION_REQUEST_TYPES } from '@app/features/contracts/validation/validation-request.model';
import { ValidationSubjectItem } from '@app/features/contracts/validation/validation-subject.model';
import { acceptedDocumentsMimeTypes, CdTDocument } from '@app/features/documents/document.model';
import { UploadDocumentButton } from '@app/features/documents/upload-document-button/upload-document-button';
import { UserRole } from '@app/features/users/user.model';
import { HasRoleDirective } from '@app/shared/auth/has-role.directive';
import { AsyncMatIconButton } from '@app/shared/buttons/async-mat-icon-button/async-mat-icon-button';
import { YousignButton } from '@app/shared/buttons/yousign-button/yousign-button';
import { AUTHENTICATED_USER_SERVICE } from '@app/shared/auth/authenticated-user-service/authenticated-user-service.token';
import {
  CommentDialog,
  CommentDialogData,
} from '@app/shared/dialogs/comment-dialog/comment-dialog';
import {
  ConfirmDialog,
  ConfirmDialogData,
} from '@app/shared/dialogs/confirm-dialog/confirm-dialog';
import { RxjsDirective } from '@app/shared/rxjs.directive';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faSignature } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { delay, filter, Observable, switchMap, tap } from 'rxjs';

/**
 * Les gestes sur un avenant : ceux du circuit d'un contrat (demander, accepter, refuser,
 * retirer), son aperçu, son abandon, et sa signature (RG-avenant-flot-de-validation). Les mêmes
 * dans sa fenêtre et sur sa ligne de la liste des contrats (RG-avenant-liste-des-contrats).
 */
@Component({
  selector: 'app-amendment-action-buttons',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    AsyncMatIconButton,
    ButtonCancelValidationRequest,
    ButtonRequestValidation,
    ButtonRefuseValidationRequest,
    ButtonAcceptAndRequestValidation,
    ButtonAcceptValidationRequest,
    UploadDocumentButton,
    YousignButton,
    HasRoleDirective,
    FontAwesomeModule,
  ],
  templateUrl: './amendment-action-buttons.html',
  styleUrls: ['./amendment-action-buttons.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { '[class.compact]': 'compact()' },
})
export class AmendmentActionButtons extends RxjsDirective {
  readonly amendment = input.required<AmendmentActionsSubject>();
  /**
   * Un avenant anticipé reste en brouillon tant que son contrat n'est pas signé : sa demande de
   * validation attend (RG-avenant-contrat-en-cours).
   */
  readonly contractSigned = input<boolean>(true);
  /** Sur la ligne de la liste, l'attente du contrat ne se dit que par une icône. */
  readonly compact = input<boolean>(false);
  /** Émis avec le PDF de l'avenant, à afficher par le parent. */
  readonly documentReady = output<CdTDocument>();

  private readonly amendmentService = inject(AmendmentService);
  private readonly dialog = inject(MatDialog);
  private readonly snackbar = inject(MatSnackBar);
  private readonly authenticatedUserService = inject(AUTHENTICATED_USER_SERVICE);

  readonly awaitingContractSignature =
    "Le contrat n'est pas encore signé : l'avenant reste en brouillon jusqu'à la signature du contrat.";
  readonly validationRequestTypes = VALIDATION_REQUEST_TYPES;
  readonly acceptedMimeTypes = acceptedDocumentsMimeTypes;
  /** L'icône du dépôt d'un contrat signé : le même geste, la même icône. */
  readonly faSignature = faSignature;
  readonly requestYousignSignatureRoles: UserRole[] = [
    'cdt-responsable-rh',
    'cdt-directeur-rh',
    'cdt-directeur-general',
  ];

  /**
   * Un brouillon s'abandonne par tout utilisateur ; un avenant en signature, par le seul directeur
   * RH (décision 2026-014/07).
   */
  readonly mayAbandon = computed(() => {
    const status = this.amendment().status;
    return (
      status === 'DRAFT' ||
      (status === 'SIGNING' && this.authenticatedUserService.hasAnyRole(['cdt-directeur-rh']))
    );
  });

  /** L'avenant, sujet du circuit de validation. */
  readonly subject = computed<ValidationSubjectItem>(() => ({
    id: this.amendment().id,
    status: this.amendment().status,
    validationRequests: this.amendment().validationRequests ?? [],
    subjectKind: 'amendment',
  }));

  getPdf(): Observable<Blob> {
    return this.amendmentService.pdf(this.amendment().id);
  }

  onPdfLoaded(blob: Blob): void {
    const amendment = this.amendment();
    const name =
      amendment.fileName ??
      (amendment.number && amendment.status !== 'DRAFT'
        ? `Avenant n°${amendment.number}`
        : 'Avenant (brouillon)');
    this.documentReady.emit({
      id: '',
      tag: 'amendment',
      name: `${name}.pdf`,
      downloadName: name,
      mimeType: 'application/pdf',
      createdAt: moment().toISOString(),
      size: blob.size,
      blob: new Blob([blob], { type: 'application/pdf' }),
    });
  }

  /** L'abandon d'un brouillon se confirme, sans motif ; celui d'un avenant en signature se motive. */
  abandon(): void {
    if (this.amendment().status === 'SIGNING') {
      this.abandonInSigning();
      return;
    }
    const dialogRef = this.dialog.open<ConfirmDialog, ConfirmDialogData, boolean>(ConfirmDialog, {
      data: {
        title: "Abandonner l'avenant ?",
        message: "Il ne s'instruira plus, et le contrat pourra recevoir un autre avenant.",
        confirmLabel: 'Abandonner',
      },
    });
    this.register(
      dialogRef
        .afterClosed()
        .pipe(
          filter((confirmed) => confirmed === true),
          switchMap(() => this.amendmentService.abandon(this.amendment().id)),
        )
        .subscribe(),
    );
  }

  private abandonInSigning(): void {
    const dialogRef = this.dialog.open<CommentDialog, CommentDialogData, string | undefined>(
      CommentDialog,
      {
        data: { title: "Motif de l'abandon de l'avenant", required: true },
        minWidth: '700px',
      },
    );
    this.register(
      dialogRef
        .afterClosed()
        .pipe(
          filter((comment): comment is string => !!comment),
          switchMap((comment) => this.amendmentService.abandon(this.amendment().id, comment)),
        )
        .subscribe(),
    );
  }

  /** Un envoi échoué s'inscrit sur l'avenant (RG-signature-echec-visible) : il se relit. */
  requestYousignSignature(): Observable<void> {
    return this.amendmentService.requestSignature(this.amendment().id).pipe(
      tap({
        error: () => this.register(this.amendmentService.findOne(this.amendment().id).subscribe()),
      }),
      delay(2000),
    );
  }

  onYousignSignatureRequested(): void {
    this.snackbar.open('Demande de signature Yousign envoyée', undefined, { duration: 3000 });
    this.register(this.amendmentService.findOne(this.amendment().id).subscribe());
  }

  onSignedDocumentUploaded(documentId: string): void {
    this.register(
      this.amendmentService
        .addSignedDocument(this.amendment().id, { tag: 'signedAmendment', id: documentId })
        .subscribe(),
    );
  }
}
